import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { X, ArrowLeft } from 'lucide-react';
import { usePdfExportStateV2, SectionData } from '../../hooks/usePdfExportStateV2';
import { useTwoPhaseRender } from '../../hooks/useTwoPhaseRender';
import { SectionRenderer } from './SectionRenderer';
import { SectionSidebar } from './SectionSidebar';
import { PreviewToolbar } from './PreviewToolbar';
import { PagesContainer, PagesContainerRef } from './PagesContainer';
import { resolveLogoPlacement } from '../../utils/logoPlacement';
import { isSuiteEnvelope, parseSuiteEnvelopeToSections } from './SuiteSections';

// Parse universalJsonResponse into flat section list
function parseDataToSections(data: any): SectionData[] {
  // BETA suite envelopes share one shape across every suite — handled by
  // their own section types, then the standard closing disclaimer.
  if (isSuiteEnvelope(data)) {
    const suiteSections = parseSuiteEnvelopeToSections(data);
    suiteSections.push({
      id: 'disclaimer',
      title: 'Professional Disclaimer',
      type: 'disclaimer',
      isVisible: true,
      orderIndex: suiteSections.length,
    });
    return suiteSections;
  }

  const sections: SectionData[] = [];
  let orderIndex = 0;

  // Debug logging for insurance_requirements
  console.log('📋 parseDataToSections DEBUG:', {
    workflowType: data?.responseMetadata?.workflowType,
    hasExecutiveSummary: !!data?.executiveSummary,
    hasOverallStatus: !!data?.executiveSummary?.overallStatus,
    hasComplianceMatrix: !!data?.complianceMatrix,
    complianceMatrixLength: data?.complianceMatrix?.length || 0,
    hasInterconnectedAnalysis: !!data?.interconnectedAnalysis,
    hasDecisionPathway: !!data?.decisionPathway,
    hasBlindSpotAcknowledgement: !!data?.blindSpotAcknowledgement,
    hasConfidenceAssessment: !!data?.confidenceAssessment,
    hasProfessionalDisclaimer: !!data?.professionalDisclaimer,
    allTopLevelKeys: data ? Object.keys(data) : [],
    // Client Renewal Presentation specific
    hasCompanyContext: !!data?.companyContext,
    hasDocumentInventory: !!data?.documentInventory,
    documentInventoryLength: data?.documentInventory?.length || 0,
    hasProgramOverview: !!data?.programOverview,
    hasCoverageHighlights: !!data?.coverageHighlights,
    coverageHighlightsLength: data?.coverageHighlights?.length || 0,
    hasRestrictiveEndorsements: !!data?.restrictiveEndorsements,
    hasPremiumAnalysis: !!data?.premiumAnalysis,
    hasCoverageChanges: !!data?.coverageChanges,
    coverageChangesLength: data?.coverageChanges?.length || 0,
    hasRedFlags: !!data?.redFlags,
    redFlagsLength: data?.redFlags?.length || 0,
    hasPositiveSignals: !!data?.positiveSignals,
    hasActionItems: !!data?.actionItems,
    actionItemsLength: data?.actionItems?.length || 0,
    hasConversationPoints: !!data?.conversationPoints,
    hasGlossary: !!data?.glossary,
    hasConfidenceAndLimitations: !!data?.confidenceAndLimitations
  });

  // Executive Summary
  if (data?.executiveSummary) {
    sections.push({
      id: 'executive-summary',
      title: 'Executive Summary',
      type: 'executive-summary',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Research Overview (Research workflow)
  if (data?.researchOverview) {
    sections.push({
      id: 'research-overview',
      title: 'Research Overview',
      type: 'research-overview',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Analysis Matrices - flatten into individual sections
  if (data?.analysisMatrices && Array.isArray(data.analysisMatrices)) {
    data.analysisMatrices.forEach((matrix: any, index: number) => {
      sections.push({
        id: `analysis-matrix-${index}`,
        title: matrix.title || `Analysis Matrix ${index + 1}`,
        type: 'analysis-matrix',
        matrixIndex: index,
        isVisible: true,
        orderIndex: orderIndex++
      });
    });
  }

  // Early detection of workflow types to exclude from generic sections
  const isPortfolioAnalyzerEarly = data?.responseMetadata?.workflowType === 'portfolio_analyzer' || (data?.portfolioStructure && data?.scenarioAnalysis);
  const isRateAnalysisEarly = data?.responseMetadata?.workflowType === 'rate_analysis' || (data?.analysisTables && data?.driverHierarchy);

  // Key Findings (EXCLUDE rate_analysis - it has its own keyFindings structure)
  if (data?.keyFindings && !isRateAnalysisEarly) {
    sections.push({
      id: 'key-findings',
      title: 'Key Findings',
      type: 'key-findings',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Recommendations (EXCLUDE portfolio_analyzer and rate_analysis - they have their own sections)
  //
  // Like schedules, recommendations is a list of items that gets cut mid-item
  // by the V2 paginator when bundled into a single tall section. We emit a
  // header anchor + one `recommendation-item` section per recommendation so
  // page breaks land between items.
  if (data?.recommendations && data.recommendations.length > 0 && !isPortfolioAnalyzerEarly && !isRateAnalysisEarly) {
    sections.push({
      id: 'recommendations',
      title: 'Recommendations',
      type: 'recommendations-header',
      isVisible: true,
      orderIndex: orderIndex++
    });
    data.recommendations.forEach((rec: any, i: number) => {
      const itemTitle = rec?.action
        ? `${rec.priority ? rec.priority + ' — ' : ''}${(typeof rec.action === 'string' ? rec.action : rec.action?.text || `Recommendation ${i + 1}`).slice(0, 60)}`
        : `Recommendation ${i + 1}`;
      sections.push({
        id: `recommendation-item-${i}`,
        title: itemTitle,
        type: 'recommendation-item',
        matrixIndex: i,
        isVisible: true,
        orderIndex: orderIndex++
      });
    });
  }

  // Strategic Considerations
  if (data?.strategicConsiderations && data.strategicConsiderations.length > 0) {
    sections.push({
      id: 'strategic-considerations',
      title: 'Strategic Considerations',
      type: 'strategic-considerations',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Risk Mitigation Strategies
  if (data?.riskMitigationStrategies && data.riskMitigationStrategies.length > 0) {
    sections.push({
      id: 'risk-mitigation-strategies',
      title: 'Risk Mitigation Strategies',
      type: 'risk-mitigation-strategies',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // === Document Comparison / Policy Comparison Sections ===

  // Non-Standard Provisions
  if (data?.nonStandardProvisions && data.nonStandardProvisions.identified && data.nonStandardProvisions.provisions && data.nonStandardProvisions.provisions.length > 0) {
    sections.push({
      id: 'non-standard-provisions',
      title: 'Non-Standard Provisions',
      type: 'non-standard-provisions',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Clause Wording Comparison
  if (data?.clauseWordingComparison && (data.clauseWordingComparison.comparisons || data.clauseWordingComparison.analysis)) {
    sections.push({
      id: 'clause-wording-comparison',
      title: 'Clause Wording Comparison',
      type: 'clause-wording-comparison',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Coverage Analysis Matrices
  if (data?.coverageAnalysisMatrices && Array.isArray(data.coverageAnalysisMatrices) && data.coverageAnalysisMatrices.length > 0) {
    sections.push({
      id: 'coverage-analysis-matrices',
      title: 'Coverage Analysis',
      type: 'coverage-analysis-matrices',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Coverage Recommendations
  if (data?.coverageRecommendations && Array.isArray(data.coverageRecommendations) && data.coverageRecommendations.length > 0) {
    sections.push({
      id: 'coverage-recommendations',
      title: 'Coverage Recommendations',
      type: 'coverage-recommendations',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Coverage Gaps and Opportunities
  if (data?.coverageGapsAndOpportunities) {
    const hasGaps = Array.isArray(data.coverageGapsAndOpportunities)
      ? data.coverageGapsAndOpportunities.length > 0
      : (data.coverageGapsAndOpportunities.gaps || data.coverageGapsAndOpportunities.opportunities);
    if (hasGaps) {
      sections.push({
        id: 'coverage-gaps-opportunities',
        title: 'Coverage Gaps & Opportunities',
        type: 'coverage-gaps-opportunities',
        isVisible: true,
        orderIndex: orderIndex++
      });
    }
  }

  // Data Quality Assessment (Policy/Document Comparison)
  if (data?.dataQualityAssessment) {
    sections.push({
      id: 'data-quality-assessment',
      title: 'Data Quality Assessment',
      type: 'data-quality-assessment',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Risk Shift Navigator
  if (data?.riskShift) {
    sections.push({
      id: 'risk-shift-navigator',
      title: 'Risk Shift Navigator',
      type: 'risk-shift-navigator',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // === Loss Run Analyzer Sections ===
  const isLossRunAnalyzer = data?.responseMetadata?.workflowType === 'loss_run_analyzer';

  // Document Manifest (Loss Run Analyzer)
  if (data?.documentManifest && Array.isArray(data.documentManifest) && data.documentManifest.length > 0) {
    sections.push({
      id: 'document-manifest',
      title: 'Analyzed Documents',
      type: 'document-manifest',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Claims Table (Loss Run Analyzer)
  if (data?.claims && Array.isArray(data.claims) && data.claims.length > 0) {
    sections.push({
      id: 'claims-table',
      title: `All Claims (${data.claims.length})`,
      type: 'claims-table',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Validation Results (Loss Run Analyzer)
  if (data?.validationResults && Array.isArray(data.validationResults) && data.validationResults.length > 0) {
    sections.push({
      id: 'validation-results',
      title: 'Cross-Foot Validation',
      type: 'validation-results',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Claim Development (Loss Run Analyzer)
  if (data?.claimDevelopment && Array.isArray(data.claimDevelopment) && data.claimDevelopment.length > 0) {
    sections.push({
      id: 'claim-development',
      title: 'Claim Development',
      type: 'claim-development',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Frequency Analysis (Loss Run Analyzer)
  if (data?.frequencyAnalysis && (data.frequencyAnalysis.claimsPerPeriod || data.frequencyAnalysis.claimsByLOB)) {
    sections.push({
      id: 'frequency-analysis',
      title: 'Frequency Analysis',
      type: 'frequency-analysis',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Severity Analysis (Loss Run Analyzer)
  if (data?.severityAnalysis && (data.severityAnalysis.distribution || data.severityAnalysis.averageClaimSize)) {
    sections.push({
      id: 'severity-analysis',
      title: 'Severity Analysis',
      type: 'severity-analysis',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Top Claims (Loss Run Analyzer)
  if (data?.topClaims && Array.isArray(data.topClaims) && data.topClaims.length > 0) {
    sections.push({
      id: 'top-claims',
      title: `Top ${data.topClaims.length} Claims`,
      type: 'top-claims',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Data Quality (Loss Run Analyzer)
  if (data?.dataQuality) {
    sections.push({
      id: 'data-quality',
      title: 'Data Quality Assessment',
      type: 'data-quality',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // === YOY Renewal Sections ===
  // Strategic Action Plan (YOY Renewal)
  if (data?.strategicActionPlan) {
    sections.push({
      id: 'strategic-action-plan',
      title: 'Strategic Action Plan',
      type: 'strategic-action-plan',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Confidence Statement (YOY Renewal)
  if (data?.confidenceStatement) {
    sections.push({
      id: 'confidence-statement',
      title: 'Confidence Statement',
      type: 'confidence-statement',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // === YOY Renewal opt-in add-on sections (Report Options step) ===
  // Each block lives under reportAddOns.* and was produced by a separate AI
  // call (the base yoy_renewal schema is at Vertex's complexity ceiling).
  //
  // CRITICAL: each schedule and each coverageDetails sub-block is emitted as
  // its own section. The V2 paginator pixel-splits oversized sections, so
  // bundling 18 schedules into one section caused page breaks to land mid-
  // row. With individual sections, the paginator splits ONLY at schedule
  // boundaries (or coverage-block boundaries) → no more mid-row cuts.
  if (data?.reportAddOns) {
    // Schedules — tolerate every shape Gemini has produced
    const rawSchedules = data.reportAddOns.schedules;
    let scheduleList: any[] = [];
    if (Array.isArray(rawSchedules)) {
      scheduleList = rawSchedules.length > 0 && rawSchedules.every((s: any) => Array.isArray(s))
        ? rawSchedules.flat()
        : rawSchedules;
    } else if (rawSchedules && Array.isArray(rawSchedules.schedules)) {
      scheduleList = rawSchedules.schedules;
    }
    if (scheduleList.length > 0) {
      // Header anchor so the TOC entry for "Schedules" still lines up
      sections.push({
        id: 'schedules-section',
        title: 'Schedules',
        type: 'schedules-header',
        isVisible: true,
        orderIndex: orderIndex++
      });
      scheduleList.forEach((schedule: any, i: number) => {
        sections.push({
          id: `schedule-item-${i}`,
          title: schedule?.scheduleType || `Schedule ${i + 1}`,
          type: 'schedule-item',
          // Carry the index so the renderer can pull the right schedule
          // out of reportAddOns.schedules at render time.
          matrixIndex: i,
          isVisible: true,
          orderIndex: orderIndex++
        });
      });
    }

    if (data.reportAddOns.coverageDetails) {
      // Header anchor
      sections.push({
        id: 'coverage-details-section',
        title: 'Coverage Details',
        type: 'coverage-details-header',
        isVisible: true,
        orderIndex: orderIndex++
      });
      // One section per sub-block — paginator splits between blocks, not
      // mid-block. Each contentKey maps to a renderer case.
      const cds: any = data.reportAddOns.coverageDetails;
      const coverageBlocks: Array<{ key: string; label: string }> = [
        { key: 'deductiblesAndRetentions', label: 'Deductibles & Retentions' },
        { key: 'subLimits', label: 'Sub-Limits' },
        { key: 'enhancementEndorsementLimits', label: 'Enhancement Endorsement Limits' },
        { key: 'exclusionsAddedRemoved', label: 'Exclusions Added / Removed' },
        { key: 'exclusionsModified', label: 'Exclusions Modified' },
        { key: 'coverageTriggers', label: 'Coverage Triggers' },
        { key: 'keyDefinitions', label: 'Key Definitions' },
      ];
      coverageBlocks.forEach(({ key, label }) => {
        // Only emit a section if the sub-block has any content
        const val = cds[key];
        const isMatrixWithRows = val && typeof val === 'object' && Array.isArray(val.rows) && val.rows.length > 0;
        const isNonEmptyString = typeof val === 'string' && val.trim().length > 0;
        const isNonEmptyArray = Array.isArray(val) && val.length > 0;
        // Special case — exclusionsAddedRemoved combines two arrays
        const isAddedRemoved = key === 'exclusionsAddedRemoved' && (
          (Array.isArray(cds.exclusionsAdded) && cds.exclusionsAdded.length > 0) ||
          (Array.isArray(cds.exclusionsRemoved) && cds.exclusionsRemoved.length > 0)
        );
        if (isMatrixWithRows || isNonEmptyString || isNonEmptyArray || isAddedRemoved) {
          sections.push({
            id: `coverage-${key}`,
            title: label,
            type: 'coverage-details-block',
            contentKey: key,
            isVisible: true,
            orderIndex: orderIndex++
          });
        }
      });
    }

    if (data.reportAddOns.complianceContractual) {
      // Header anchor
      sections.push({
        id: 'compliance-contractual-section',
        title: 'Compliance & Contractual',
        type: 'compliance-contractual-header',
        isVisible: true,
        orderIndex: orderIndex++
      });
      const cc: any = data.reportAddOns.complianceContractual;
      // Split into 3 sub-blocks so each gets its own page if needed
      sections.push({
        id: 'compliance-summary',
        title: 'Compliance Summary',
        type: 'compliance-block',
        contentKey: 'summary',
        isVisible: true,
        orderIndex: orderIndex++
      });
      if (cc.coiRequirements && (
        (typeof cc.coiRequirements === 'object' && Array.isArray(cc.coiRequirements.rows) && cc.coiRequirements.rows.length > 0) ||
        (typeof cc.coiRequirements === 'string' && cc.coiRequirements.trim())
      )) {
        sections.push({
          id: 'compliance-coi',
          title: 'COI Requirements vs Policy',
          type: 'compliance-block',
          contentKey: 'coiRequirements',
          isVisible: true,
          orderIndex: orderIndex++
        });
      }
      if (cc.contractualObligations && (
        (typeof cc.contractualObligations === 'object' && Array.isArray(cc.contractualObligations.rows) && cc.contractualObligations.rows.length > 0) ||
        (typeof cc.contractualObligations === 'string' && cc.contractualObligations.trim())
      )) {
        sections.push({
          id: 'compliance-obligations',
          title: 'Contractual Obligations',
          type: 'compliance-block',
          contentKey: 'contractualObligations',
          isVisible: true,
          orderIndex: orderIndex++
        });
      }
    }
  }

  // === Insurance Requirements v2.0 Sections ===
  const isInsuranceRequirements = data?.responseMetadata?.workflowType === 'insurance_requirements';

  // Insurance Requirements - Executive Summary (special handling for overallStatus)
  if (isInsuranceRequirements && data?.executiveSummary?.overallStatus) {
    // Skip generic executive-summary, use insurance-specific one
    const existingExecSummaryIndex = sections.findIndex(s => s.id === 'executive-summary');
    if (existingExecSummaryIndex >= 0) {
      sections.splice(existingExecSummaryIndex, 1);
    }
    sections.push({
      id: 'insurance-executive-summary',
      title: 'Executive Summary',
      type: 'insurance-executive-summary',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Insurance Requirements - Compliance Matrix
  if (data?.complianceMatrix && Array.isArray(data.complianceMatrix) && data.complianceMatrix.length > 0) {
    sections.push({
      id: 'compliance-matrix',
      title: 'Compliance Matrix',
      type: 'compliance-matrix',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Insurance Requirements - Interconnected Analysis
  if (data?.interconnectedAnalysis) {
    sections.push({
      id: 'interconnected-analysis',
      title: 'Interconnected Risk Analysis',
      type: 'interconnected-analysis',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Insurance Requirements - Decision Pathway
  if (data?.decisionPathway) {
    sections.push({
      id: 'decision-pathway',
      title: 'Decision Pathway',
      type: 'decision-pathway',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Insurance Requirements - Blind Spot Acknowledgement
  if (data?.blindSpotAcknowledgement) {
    sections.push({
      id: 'blind-spot-acknowledgement',
      title: 'Limitations & Blind Spots',
      type: 'blind-spot-acknowledgement',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Insurance Requirements - Confidence Assessment
  if (data?.confidenceAssessment) {
    sections.push({
      id: 'confidence-assessment',
      title: 'Confidence Assessment',
      type: 'confidence-assessment',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // === Commercial Lines Executive Renewal Sections ===
  const isCommercialLinesExecRenewal = data?.responseMetadata?.workflowType === 'commercial_lines_exec_renewal';

  // Commercial Lines - Executive Renewal Summary (Deliverable A)
  if (data?.executiveRenewalSummary) {
    sections.push({
      id: 'executive-renewal-summary',
      title: 'Executive Renewal Summary',
      type: 'executive-renewal-summary',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Commercial Lines - Renewal Program Matrix (part of executiveRenewalSummary but shown separately)
  if (data?.executiveRenewalSummary?.renewalProgramMatrix && Array.isArray(data.executiveRenewalSummary.renewalProgramMatrix) && data.executiveRenewalSummary.renewalProgramMatrix.length > 0) {
    sections.push({
      id: 'renewal-program-matrix',
      title: 'Renewal Program Matrix',
      type: 'renewal-program-matrix',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Commercial Lines - Risk Intelligence Report (Deliverable B)
  if (data?.riskIntelligenceReport) {
    // Entity Overview
    if (data.riskIntelligenceReport.entityOverview) {
      sections.push({
        id: 'entity-overview',
        title: 'Entity Overview',
        type: 'entity-overview',
        isVisible: true,
        orderIndex: orderIndex++
      });
    }

    // Research Methodology
    if (data.riskIntelligenceReport.researchMethodology) {
      sections.push({
        id: 'research-methodology',
        title: 'Research Methodology',
        type: 'research-methodology',
        isVisible: true,
        orderIndex: orderIndex++
      });
    }

    // Critical Path Priorities
    if (data.riskIntelligenceReport.criticalPathPriorities && Array.isArray(data.riskIntelligenceReport.criticalPathPriorities) && data.riskIntelligenceReport.criticalPathPriorities.length > 0) {
      sections.push({
        id: 'critical-path-priorities',
        title: 'Critical Path Priorities',
        type: 'critical-path-priorities',
        isVisible: true,
        orderIndex: orderIndex++
      });
    }

    // Comprehensive Risk Assessment
    if (data.riskIntelligenceReport.comprehensiveRiskAssessment && Array.isArray(data.riskIntelligenceReport.comprehensiveRiskAssessment) && data.riskIntelligenceReport.comprehensiveRiskAssessment.length > 0) {
      sections.push({
        id: 'comprehensive-risk-assessment',
        title: 'Comprehensive Risk Assessment',
        type: 'comprehensive-risk-assessment',
        isVisible: true,
        orderIndex: orderIndex++
      });
    }

    // Coverage Applicability Analysis
    if (data.riskIntelligenceReport.coverageApplicabilityAnalysis) {
      sections.push({
        id: 'coverage-applicability-analysis',
        title: 'Coverage Applicability Analysis',
        type: 'coverage-applicability-analysis',
        isVisible: true,
        orderIndex: orderIndex++
      });
    }

    // Risk Mitigation Framework
    if (data.riskIntelligenceReport.riskMitigationFramework && Array.isArray(data.riskIntelligenceReport.riskMitigationFramework) && data.riskIntelligenceReport.riskMitigationFramework.length > 0) {
      sections.push({
        id: 'risk-mitigation-framework',
        title: 'Risk Mitigation Framework',
        type: 'risk-mitigation-framework',
        isVisible: true,
        orderIndex: orderIndex++
      });
    }

    // Risk Correlation Map
    if (data.riskIntelligenceReport.riskCorrelationMap && Array.isArray(data.riskIntelligenceReport.riskCorrelationMap) && data.riskIntelligenceReport.riskCorrelationMap.length > 0) {
      sections.push({
        id: 'risk-correlation-map',
        title: 'Risk Correlation Map',
        type: 'risk-correlation-map',
        isVisible: true,
        orderIndex: orderIndex++
      });
    }
  }

  // Commercial Lines - Confidence & Limitations
  if (data?.confidenceAndLimitations) {
    sections.push({
      id: 'confidence-and-limitations',
      title: 'Confidence & Limitations',
      type: 'confidence-and-limitations',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // === Client Renewal Presentation Sections ===
  const isClientRenewalPresentation = data?.responseMetadata?.workflowType === 'client_renewal_presentation' || (data?.coverageHighlights && data?.restrictiveEndorsements);
  console.log('📋 isClientRenewalPresentation check:', {
    isClientRenewalPresentation,
    workflowTypeMatch: data?.responseMetadata?.workflowType === 'client_renewal_presentation',
    dataStructureMatch: !!(data?.coverageHighlights && data?.restrictiveEndorsements)
  });

  // Client Renewal Presentation - Executive Summary (special handling for text/keyHighlights format)
  if (isClientRenewalPresentation && data?.executiveSummary?.text) {
    // Remove generic executive-summary, replace with client-renewal-specific one
    const existingExecSummaryIndex = sections.findIndex(s => s.id === 'executive-summary');
    if (existingExecSummaryIndex >= 0) {
      sections.splice(existingExecSummaryIndex, 1);
    }
    sections.push({
      id: 'client-renewal-executive-summary',
      title: 'Executive Summary',
      type: 'client-renewal-executive-summary',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Client Renewal - Company Context
  if (data?.companyContext && isClientRenewalPresentation) {
    sections.push({
      id: 'company-context',
      title: 'Company Context',
      type: 'company-context',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Client Renewal - Document Inventory
  if (data?.documentInventory && Array.isArray(data.documentInventory) && data.documentInventory.length > 0 && isClientRenewalPresentation) {
    sections.push({
      id: 'document-inventory',
      title: 'Document Inventory',
      type: 'document-inventory',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Client Renewal - Program Overview
  if (data?.programOverview && isClientRenewalPresentation) {
    sections.push({
      id: 'program-overview',
      title: 'Program Overview',
      type: 'program-overview',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Client Renewal - Coverage Highlights
  if (data?.coverageHighlights && Array.isArray(data.coverageHighlights) && data.coverageHighlights.length > 0) {
    sections.push({
      id: 'coverage-highlights',
      title: 'Coverage Highlights',
      type: 'coverage-highlights',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Client Renewal - Restrictive Endorsements
  if (data?.restrictiveEndorsements) {
    sections.push({
      id: 'restrictive-endorsements',
      title: 'Restrictive Endorsements',
      type: 'restrictive-endorsements',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Client Renewal - Premium Analysis
  if (data?.premiumAnalysis && isClientRenewalPresentation) {
    sections.push({
      id: 'premium-analysis',
      title: 'Premium Analysis',
      type: 'premium-analysis',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Client Renewal - Coverage Changes
  if (data?.coverageChanges && Array.isArray(data.coverageChanges) && data.coverageChanges.length > 0) {
    sections.push({
      id: 'coverage-changes',
      title: 'Coverage Changes',
      type: 'coverage-changes',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Client Renewal - Red Flags
  if (data?.redFlags && Array.isArray(data.redFlags) && data.redFlags.length > 0) {
    sections.push({
      id: 'red-flags',
      title: 'Red Flags',
      type: 'red-flags',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Client Renewal - Positive Signals
  if (data?.positiveSignals && Array.isArray(data.positiveSignals) && data.positiveSignals.length > 0) {
    sections.push({
      id: 'positive-signals',
      title: 'Positive Signals',
      type: 'positive-signals',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Client Renewal - Action Items
  if (data?.actionItems && Array.isArray(data.actionItems) && data.actionItems.length > 0 && isClientRenewalPresentation) {
    sections.push({
      id: 'action-items',
      title: 'Action Items',
      type: 'action-items',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Client Renewal - Conversation Points
  if (data?.conversationPoints && Array.isArray(data.conversationPoints) && data.conversationPoints.length > 0) {
    sections.push({
      id: 'conversation-points',
      title: 'Conversation Points',
      type: 'conversation-points',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Client Renewal - Glossary
  if (data?.glossary && Array.isArray(data.glossary) && data.glossary.length > 0) {
    sections.push({
      id: 'glossary',
      title: 'Glossary',
      type: 'glossary',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // === Financial Analysis Sections ===
  const isFinancialAnalysis = data?.responseMetadata?.workflowType === 'financial_analysis';

  // Financial Analysis - Financial Risk Assessment
  if (data?.financialRiskAssessment) {
    sections.push({
      id: 'financial-risk-assessment',
      title: 'Financial Risk Assessment',
      type: 'financial-risk-assessment',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Financial Analysis - Detailed Analysis
  if (data?.detailedAnalysis) {
    sections.push({
      id: 'detailed-financial-analysis',
      title: 'Detailed Financial Analysis',
      type: 'detailed-financial-analysis',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Financial Analysis - Period Comparison (it's an object with periodsAnalyzed, revenueMetrics, etc.)
  if (data?.periodComparison && (data.periodComparison.periodsAnalyzed || data.periodComparison.revenueMetrics)) {
    sections.push({
      id: 'period-comparison',
      title: 'Period Comparison',
      type: 'period-comparison',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Financial Analysis - ERM Risk Mapping
  if (data?.ermRiskMapping) {
    sections.push({
      id: 'erm-risk-mapping',
      title: 'Enterprise Risk Mapping',
      type: 'erm-risk-mapping',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Financial Analysis - Underwriting Recommendation
  if (data?.underwritingRecommendation) {
    sections.push({
      id: 'underwriting-recommendation',
      title: 'Underwriting Recommendation',
      type: 'underwriting-recommendation',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // === Coverage Analysis Sections ===
  const isCoverageAnalysis = data?.responseMetadata?.workflowType === 'coverage_analysis' || (data?.policyOverview && data?.coverageMetrics);

  // Coverage Analysis - Policy Overview
  if (data?.policyOverview && isCoverageAnalysis) {
    sections.push({
      id: 'policy-overview',
      title: 'Policy Overview',
      type: 'policy-overview',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Coverage Analysis - Coverage Recommendations
  if (data?.coverageRecommendations && Array.isArray(data.coverageRecommendations) && data.coverageRecommendations.length > 0) {
    sections.push({
      id: 'coverage-recommendations',
      title: 'Coverage Recommendations',
      type: 'coverage-recommendations',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Coverage Analysis - Coverage Gaps and Opportunities
  if (data?.coverageGapsAndOpportunities) {
    sections.push({
      id: 'coverage-gaps-opportunities',
      title: 'Coverage Gaps & Opportunities',
      type: 'coverage-gaps-opportunities',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Coverage Analysis - Scenario Testing
  if (data?.scenarioTesting) {
    sections.push({
      id: 'scenario-testing',
      title: 'Scenario Testing',
      type: 'scenario-testing',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Coverage Analysis - Coverage Metrics
  if (data?.coverageMetrics) {
    sections.push({
      id: 'coverage-metrics',
      title: 'Coverage Metrics',
      type: 'coverage-metrics',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Coverage Analysis - Meta Analysis
  if (data?.metaAnalysis) {
    sections.push({
      id: 'meta-analysis',
      title: 'Meta Analysis',
      type: 'meta-analysis',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // === Portfolio Analyzer Sections ===
  const isPortfolioAnalyzer = data?.responseMetadata?.workflowType === 'portfolio_analyzer' || (data?.portfolioStructure && data?.scenarioAnalysis);

  // Portfolio Analyzer - Executive Summary (special handling for tldr/bottomLine format)
  if (isPortfolioAnalyzer && data?.executiveSummary?.tldr) {
    // Remove generic executive-summary, replace with portfolio-specific one
    const existingExecSummaryIndex = sections.findIndex(s => s.id === 'executive-summary');
    if (existingExecSummaryIndex >= 0) {
      sections.splice(existingExecSummaryIndex, 1);
    }
    sections.push({
      id: 'portfolio-executive-summary',
      title: 'Executive Summary',
      type: 'portfolio-executive-summary',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Portfolio Analyzer - Portfolio Structure
  if (data?.portfolioStructure && isPortfolioAnalyzer) {
    sections.push({
      id: 'portfolio-structure',
      title: 'Portfolio Structure',
      type: 'portfolio-structure',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Portfolio Analyzer - Scenario Analysis
  if (data?.scenarioAnalysis && isPortfolioAnalyzer) {
    sections.push({
      id: 'scenario-analysis',
      title: 'Scenario Analysis',
      type: 'scenario-analysis',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Portfolio Analyzer - Coverage Analysis (note: this is different from coverage_analysis workflow)
  if (data?.coverageAnalysis && Array.isArray(data.coverageAnalysis) && data.coverageAnalysis.length > 0 && isPortfolioAnalyzer) {
    sections.push({
      id: 'portfolio-coverage-analysis',
      title: 'Coverage Analysis',
      type: 'portfolio-coverage-analysis',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Portfolio Analyzer - Portfolio Synthesis
  if (data?.portfolioSynthesis && isPortfolioAnalyzer) {
    sections.push({
      id: 'portfolio-synthesis',
      title: 'Portfolio Synthesis',
      type: 'portfolio-synthesis',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Portfolio Analyzer - Assessment
  if (data?.assessment && isPortfolioAnalyzer) {
    sections.push({
      id: 'portfolio-assessment',
      title: 'Assessment',
      type: 'portfolio-assessment',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Portfolio Analyzer - Recommendations (after Assessment, before Blind Spots)
  if (data?.recommendations && Array.isArray(data.recommendations) && data.recommendations.length > 0 && isPortfolioAnalyzer) {
    sections.push({
      id: 'portfolio-recommendations',
      title: 'Recommendations',
      type: 'recommendations',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Portfolio Analyzer - Blind Spots
  if (data?.blindSpots && isPortfolioAnalyzer) {
    sections.push({
      id: 'blind-spots',
      title: 'Blind Spots & Limitations',
      type: 'blind-spots',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // === Rate Analysis Sections ===
  // For Rate Analysis, we need to replace generic sections with rate-specific ones AND ensure correct ordering
  const isRateAnalysis = data?.responseMetadata?.workflowType === 'rate_analysis' || (data?.analysisTables && data?.driverHierarchy);

  if (isRateAnalysis) {
    // Clear all generic sections and rebuild with rate-analysis-specific order
    // Remove any generic sections that were added (including recommendations)
    const genericSectionsToRemove = ['executive-summary', 'key-findings', 'analysis-matrices', 'recommendations'];
    genericSectionsToRemove.forEach(id => {
      const idx = sections.findIndex(s => s.id === id);
      if (idx >= 0) sections.splice(idx, 1);
    });

    // Reset orderIndex for rate analysis to ensure proper ordering
    let rateOrderIndex = 1; // Start at 1 (after any metadata)

    // Rate Analysis - Executive Summary
    if (data?.executiveSummary) {
      sections.push({
        id: 'rate-analysis-executive-summary',
        title: 'Executive Summary',
        type: 'rate-analysis-executive-summary',
        isVisible: true,
        orderIndex: rateOrderIndex++
      });
    }

    // Rate Analysis - Analysis Tables
    if (data?.analysisTables && Array.isArray(data.analysisTables) && data.analysisTables.length > 0) {
      sections.push({
        id: 'rate-analysis-tables',
        title: 'Analysis Tables',
        type: 'rate-analysis-tables',
        isVisible: true,
        orderIndex: rateOrderIndex++
      });
    }

    // Rate Analysis - Driver Hierarchy
    if (data?.driverHierarchy) {
      sections.push({
        id: 'rate-driver-hierarchy',
        title: 'Driver Hierarchy',
        type: 'rate-driver-hierarchy',
        isVisible: true,
        orderIndex: rateOrderIndex++
      });
    }

    // Rate Analysis - Weak Signals
    if (data?.weakSignals && Array.isArray(data.weakSignals) && data.weakSignals.length > 0) {
      sections.push({
        id: 'rate-weak-signals',
        title: 'Weak Signals',
        type: 'rate-weak-signals',
        isVisible: true,
        orderIndex: rateOrderIndex++
      });
    }

    // Rate Analysis - Trend Analysis
    if (data?.trendAnalysis) {
      sections.push({
        id: 'rate-trend-analysis',
        title: 'Trend Analysis',
        type: 'rate-trend-analysis',
        isVisible: true,
        orderIndex: rateOrderIndex++
      });
    }

    // Rate Analysis - Key Findings
    if (data?.keyFindings) {
      sections.push({
        id: 'rate-key-findings',
        title: 'Key Findings',
        type: 'rate-key-findings',
        isVisible: true,
        orderIndex: rateOrderIndex++
      });
    }

    // Rate Analysis - Recommendations
    if (data?.recommendations && Array.isArray(data.recommendations) && data.recommendations.length > 0) {
      sections.push({
        id: 'rate-recommendations',
        title: 'Recommendations',
        type: 'recommendations',
        isVisible: true,
        orderIndex: rateOrderIndex++
      });
    }

    // Update global orderIndex for any subsequent sections (like disclaimer)
    orderIndex = rateOrderIndex;
  }

  // === Personal Umbrella / Homeowners Comparison Sections ===
  const isUmbrellaOrHomeowners = data?.responseMetadata?.workflowType === 'personal_umbrella' ||
    data?.responseMetadata?.workflowType === 'homeowners_comparison' ||
    (data?.baselineExtraction && data?.gapSynthesis);

  if (isUmbrellaOrHomeowners) {
    if (data?.analysisHeader) {
      sections.push({ id: 'umbrella-analysis-header', title: 'Analysis Overview', type: 'umbrella-generic', contentKey: 'analysisHeader', isVisible: true, orderIndex: orderIndex++ });
    }
    if (data?.documentClassification?.length) {
      sections.push({ id: 'umbrella-doc-classification', title: 'Document Classification', type: 'umbrella-generic', contentKey: 'documentClassification', isVisible: true, orderIndex: orderIndex++ });
    }
    if (data?.modeRationale) {
      sections.push({ id: 'umbrella-mode-rationale', title: 'Mode Rationale', type: 'umbrella-generic', contentKey: 'modeRationale', isVisible: true, orderIndex: orderIndex++ });
    }
    if (data?.temporalVerification) {
      sections.push({ id: 'umbrella-temporal', title: 'Temporal Verification', type: 'umbrella-generic', contentKey: 'temporalVerification', isVisible: true, orderIndex: orderIndex++ });
    }
    if (data?.policyArchitecture) {
      sections.push({ id: 'umbrella-policy-arch', title: 'Policy Architecture', type: 'umbrella-generic', contentKey: 'policyArchitecture', isVisible: true, orderIndex: orderIndex++ });
    }
    if (data?.scheduleOfForms?.length) {
      sections.push({ id: 'umbrella-schedule-forms', title: 'Schedule of Forms', type: 'umbrella-generic', contentKey: 'scheduleOfForms', isVisible: true, orderIndex: orderIndex++ });
    }
    if (data?.baselineExtraction?.length) {
      sections.push({ id: 'umbrella-baseline', title: 'Baseline Extraction', type: 'umbrella-generic', contentKey: 'baselineExtraction', isVisible: true, orderIndex: orderIndex++ });
    }
    if (data?.exposureProfile) {
      sections.push({ id: 'umbrella-exposure', title: 'Exposure Profile', type: 'umbrella-generic', contentKey: 'exposureProfile', isVisible: true, orderIndex: orderIndex++ });
    }
    if (data?.dropDownMatrix) {
      sections.push({ id: 'umbrella-dropdown-matrix', title: 'Drop-Down Coverage Matrix', type: 'umbrella-generic', contentKey: 'dropDownMatrix', isVisible: true, orderIndex: orderIndex++ });
    }
    if (data?.coverageAnalysis) {
      sections.push({ id: 'umbrella-coverage', title: 'Coverage Analysis', type: 'umbrella-generic', contentKey: 'coverageAnalysis', isVisible: true, orderIndex: orderIndex++ });
    }
    if (data?.compoundFailures) {
      sections.push({ id: 'umbrella-compound-failures', title: 'Compound Failure Analysis', type: 'umbrella-generic', contentKey: 'compoundFailures', isVisible: true, orderIndex: orderIndex++ });
    }
    if (data?.appearanceOfCoverage) {
      sections.push({ id: 'umbrella-appearance', title: 'Appearance of Coverage Analysis', type: 'umbrella-generic', contentKey: 'appearanceOfCoverage', isVisible: true, orderIndex: orderIndex++ });
    }
    if (data?.premiumAnalysis) {
      sections.push({ id: 'umbrella-premium', title: 'Premium Analysis', type: 'umbrella-generic', contentKey: 'premiumAnalysis', isVisible: true, orderIndex: orderIndex++ });
    }
    if (data?.gapSynthesis) {
      sections.push({ id: 'umbrella-gap-synthesis', title: 'Gap Synthesis', type: 'umbrella-generic', contentKey: 'gapSynthesis', isVisible: true, orderIndex: orderIndex++ });
    }
    if (data?.decisionFramework) {
      sections.push({ id: 'umbrella-decision', title: 'Decision Framework', type: 'umbrella-generic', contentKey: 'decisionFramework', isVisible: true, orderIndex: orderIndex++ });
    }
  }

  // === Disclaimers (always at the end) ===

  // Professional Disclaimer (Insurance Requirements / Commercial Lines)
  if (data?.professionalDisclaimer) {
    sections.push({
      id: 'professional-disclaimer',
      title: 'Professional Disclaimer',
      type: 'professional-disclaimer',
      isVisible: true,
      orderIndex: orderIndex++
    });
  }

  // Disclaimer (all workflows - always present, uses default if none from AI)
  sections.push({
    id: 'disclaimer',
    title: 'Professional Disclaimer',
    type: 'disclaimer',
    isVisible: true,
    orderIndex: orderIndex++
  });

  console.log('📋 parseDataToSections RESULT:', sections.map(s => ({ id: s.id, type: s.type })));
  return sections;
}

export const PdfExportPreviewV2: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pagesContainerRef = useRef<PagesContainerRef>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);
  const [branding, setBranding] = useState<any>(null);

  // Get export key from URL params
  const exportKey = searchParams.get('key');
  const authToken = searchParams.get('token') || localStorage.getItem('riskninja-token');

  // Load and decompress data from localStorage
  const [exportData, setExportData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadExportData = async () => {
      if (!exportKey) {
        console.error('No export key provided');
        setIsLoading(false);
        return;
      }
      try {
        const stored = localStorage.getItem(exportKey);
        const isCompressed = localStorage.getItem(`${exportKey}_compressed`) === 'true';

        if (stored) {
          let data: string;
          if (isCompressed) {
            const LZString = await import('lz-string');
            data = LZString.decompressFromUTF16(stored) || '';
            console.log('Decompressed export data from localStorage:', exportKey);
          } else {
            data = stored;
            console.log('Loaded export data from localStorage:', exportKey);
          }
          setExportData(JSON.parse(data));
        }
      } catch (e) {
        console.error('Failed to parse PDF export data:', e);
      } finally {
        setIsLoading(false);
      }
    };
    loadExportData();
  }, [exportKey]);

  // Extract universalJsonResponse from workflowData
  const universalJsonResponse = useMemo(() => {
    if (!exportData?.workflowData) return null;
    return exportData.workflowData.universalJsonResponse || null;
  }, [exportData]);

  // Extract report title
  const reportTitle = useMemo(() => {
    if (!exportData?.workflowData) return 'Analysis Report';
    return exportData.workflowData.title ||
           exportData.workflowData.reportTitle ||
           universalJsonResponse?.title ||
           universalJsonResponse?.reportTitle ||
           'Analysis Report';
  }, [exportData, universalJsonResponse]);

  // Fetch company branding
  useEffect(() => {
    if (!authToken) return;

    const fetchBranding = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const response = await fetch(`${apiUrl}/api/companies/branding`, {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          if (data?.branding) {
            setBranding(data.branding);
          }
        }
      } catch (error) {
        console.error('Failed to fetch branding:', error);
      }
    };

    fetchBranding();
  }, [authToken]);

  // Load logo from branding
  useEffect(() => {
    if (!branding?.logoUrl || !authToken) {
      setLogoDataUrl(null);
      return;
    }

    // If already a data URL, use directly
    if (branding.logoUrl.startsWith('data:')) {
      setLogoDataUrl(branding.logoUrl);
      return;
    }

    // Fetch the logo
    const fetchLogo = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const logoPath = branding.logoUrl.startsWith('/') ? branding.logoUrl : `/${branding.logoUrl}`;
        const response = await fetch(`${apiUrl}${logoPath}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });
        if (response.ok) {
          const blob = await response.blob();
          const reader = new FileReader();
          reader.onloadend = () => {
            setLogoDataUrl(reader.result as string);
          };
          reader.readAsDataURL(blob);
        }
      } catch (error) {
        console.error('Failed to fetch logo:', error);
      }
    };

    fetchLogo();
  }, [branding, authToken]);

  const companyName = branding?.companyName || '';
  const headerText = '';
  const footerText = '';
  const logoPosition = (branding?.logoPosition || 'right') as 'left' | 'center' | 'right';
  // Resolve free logo placement (x/y/width %), falling back to logoPosition.
  const logoPlacement = resolveLogoPlacement(branding || {});

  // Parse sections from data
  const initialSections = useMemo(() => {
    return parseDataToSections(universalJsonResponse);
  }, [universalJsonResponse]);

  // State management
  const {
    sections,
    pageSettings,
    zoom,
    pageBreaksAfter,
    pageBreaksBefore,
    selectedSectionId,
    sectionOffsets,
    isEditMode,
    editedContent,
    removedBlocks,
    discoveredBlocks,
    visibleSections,
    pageDimensions,
    contentDimensions,
    marginPixels,
    toggleSection,
    toggleAllSections,
    reorderSection,
    togglePageBreak,
    togglePageBreakBefore,
    setPageSettings,
    setZoom,
    resetToDefaults,
    selectSection,
    nudgeSection,
    setEditMode,
    updateEditedContent,
    toggleBlock,
    registerBlocks
  } = usePdfExportStateV2({ initialSections });

  // Calculate header height for pagination (logo and/or title)
  const hasHeader = logoDataUrl || reportTitle;

  // Two-phase rendering
  const {
    phase,
    heights,
    pageAssignments,
    measureRef,
    remeasure
  } = useTwoPhaseRender({
    sections: visibleSections,
    pageHeight: contentDimensions.height,
    headerHeight: hasHeader ? 80 : 0,
    pageBreaksAfter,
    pageBreaksBefore,
    sectionOffsets
  });

  // Trigger remeasure when content is edited (debounced)
  const remeasureTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (editedContent.size > 0) {
      // Clear any existing timeout
      if (remeasureTimeoutRef.current) {
        clearTimeout(remeasureTimeoutRef.current);
      }
      // Debounce remeasure to avoid excessive recalculations
      remeasureTimeoutRef.current = setTimeout(() => {
        remeasure();
      }, 500);
    }
    return () => {
      if (remeasureTimeoutRef.current) {
        clearTimeout(remeasureTimeoutRef.current);
      }
    };
  }, [editedContent, remeasure]);

  // Removing/restoring a colored sub-block changes section heights, so
  // re-run pagination. Skip the initial empty state (avoids an extra cycle
  // before the first measurement completes).
  const removedBlocksKey = useMemo(
    () => Array.from(removedBlocks).sort().join('|'),
    [removedBlocks]
  );
  const prevRemovedKeyRef = useRef<string>('');
  useEffect(() => {
    if (prevRemovedKeyRef.current === removedBlocksKey) return;
    prevRemovedKeyRef.current = removedBlocksKey;
    remeasure();
  }, [removedBlocksKey, remeasure]);

  // Handle page navigation from toolbar
  const handlePageChange = useCallback((pageNumber: number) => {
    pagesContainerRef.current?.scrollToPage(pageNumber);
  }, []);

  // Handle print
  const handlePrint = useCallback(() => {
    // Guard against firing window.print() before two-phase pagination
    // settles. The first measurement cycle runs with the data-less
    // placeholder section list (just the disclaimer); calling print()
    // during that window outputs a 1-page PDF with only the disclaimer.
    if (phase !== 'paginated') {
      console.warn('[PdfExportPreviewV2] Print suppressed — still measuring/paginating');
      return;
    }
    window.print();
  }, [phase]);

  // Handle close
  const handleClose = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Loading state
  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1993e5] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading export data...</p>
        </div>
      </div>
    );
  }

  // No data state
  if (!universalJsonResponse) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">No Export Data</h2>
          <p className="text-gray-600 mb-4">No report data found for export.</p>
          <button
            onClick={handleClose}
            className="flex items-center gap-2 px-4 py-2 bg-[#1993e5] text-white rounded hover:bg-[#1580c9] transition-colors mx-auto"
          >
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const totalPages = pageAssignments.length || 1;

  return (
    <div className="pdf-export-preview-v2 h-screen flex flex-col bg-gray-100">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200 shadow-sm">
        <div className="flex items-center gap-4">
          <button
            onClick={handleClose}
            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
            title="Close preview"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="text-lg font-semibold text-gray-800">PDF Export Preview</h1>
          {phase === 'measuring' && (
            <span className="text-sm text-blue-600 animate-pulse">Measuring content...</span>
          )}
        </div>
        <div className="text-sm text-gray-500">
          {sections.filter(s => s.isVisible).length} sections • {totalPages} page{totalPages !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <SectionSidebar
          sections={sections}
          pageAssignments={pageAssignments}
          onToggleSection={toggleSection}
          onToggleAll={toggleAllSections}
          onReorder={reorderSection}
          onTogglePageBreak={togglePageBreak}
          pageBreaksAfter={pageBreaksAfter}
          discoveredBlocks={discoveredBlocks}
          removedBlocks={removedBlocks}
          onToggleBlock={toggleBlock}
        />

        {/* Preview area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Toolbar */}
          <PreviewToolbar
            zoom={zoom}
            onZoomChange={setZoom}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            pageSettings={pageSettings}
            onPageSettingsChange={setPageSettings}
            onPrint={handlePrint}
            onReset={resetToDefaults}
            pageDimensions={pageDimensions}
            marginPixels={marginPixels}
            isEditMode={isEditMode}
            onEditModeChange={setEditMode}
            isPaginating={phase !== 'paginated'}
          />

          {/* Measurement container - hidden, used for Phase 1 */}
          <div
            ref={measureRef}
            className="measurement-container"
            style={{
              position: 'absolute',
              visibility: 'hidden',
              width: contentDimensions.width,
              pointerEvents: 'none',
              left: -9999,
              top: 0
            }}
          >
            {visibleSections.map(section => (
              <SectionRenderer
                key={section.id}
                section={section}
                data={universalJsonResponse}
                contentWidth={contentDimensions.width}
                isEditMode={isEditMode}
                editedContent={editedContent}
                onContentChange={updateEditedContent}
                removedBlocks={removedBlocks}
                onDiscoverBlocks={registerBlocks}
              />
            ))}
          </div>

          {/* Pages display */}
          {phase === 'paginated' ? (
            <PagesContainer
              ref={pagesContainerRef}
              pageAssignments={pageAssignments}
              sections={visibleSections}
              data={universalJsonResponse}
              contentWidth={contentDimensions.width}
              contentHeight={contentDimensions.height}
              marginPixels={marginPixels}
              pageDimensions={pageDimensions}
              zoom={zoom}
              logoDataUrl={logoDataUrl}
              logoPosition={logoPosition}
              logoPlacement={logoPlacement}
              companyName={companyName}
              headerText={headerText}
              footerText={footerText}
              reportTitle={reportTitle}
              heights={heights}
              onCurrentPageChange={setCurrentPage}
              selectedSectionId={selectedSectionId}
              onSelectSection={selectSection}
              onNudgeSection={nudgeSection}
              sectionOffsets={sectionOffsets}
              isEditMode={isEditMode}
              editedContent={editedContent}
              onContentChange={updateEditedContent}
              removedBlocks={removedBlocks}
              onDiscoverBlocks={registerBlocks}
            />
          ) : (
            // Loading state during measurement
            <div className="flex-1 flex items-center justify-center bg-gray-400">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                <p className="text-white font-medium">Calculating page layout...</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          /* Hide header bar */
          .pdf-export-preview-v2 > div:first-child {
            display: none !important;
          }

          /* Hide sidebar */
          .section-sidebar {
            display: none !important;
          }

          /* Hide toolbar */
          .preview-toolbar,
          .settings-panel {
            display: none !important;
          }

          /* Hide measurement container */
          .measurement-container {
            display: none !important;
          }

          /* Main container - allow content to flow */
          .pdf-export-preview-v2 {
            height: auto !important;
            overflow: visible !important;
            background: white !important;
          }

          /* Remove flex constraints on ALL nested containers */
          .pdf-export-preview-v2 > div {
            height: auto !important;
            overflow: visible !important;
          }

          /* Preview area container */
          .pdf-export-preview-v2 .flex-1 {
            height: auto !important;
            overflow: visible !important;
            flex: none !important;
          }

          /* Pages container - critical for showing all pages */
          .pages-container {
            height: auto !important;
            overflow: visible !important;
            padding: 0 !important;
            background: white !important;
          }

          /* Pages wrapper - remove transform/scale and gap */
          .pages-wrapper {
            transform: none !important;
            padding: 0 !important;
            gap: 0 !important;
          }

          /* Individual pages - keep their structure for absolute children */
          .pdf-page {
            box-shadow: none !important;
            margin: 0 auto !important;
            page-break-after: always !important;
            break-after: page !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            position: relative !important;
          }

          .pdf-page:last-child {
            page-break-after: auto !important;
            break-after: auto !important;
          }

          /* Hide info panel at bottom */
          .pages-wrapper > div.text-center {
            display: none !important;
          }

          /* Hide page number badge */
          .pdf-page > div.absolute.-top-3 {
            display: none !important;
          }

          /* Hide margin indicator dashed border */
          .pdf-page > div.absolute.pointer-events-none {
            display: none !important;
          }

          /* Hide nudge controls */
          .pdf-section-wrapper .z-30 {
            display: none !important;
          }

          /* Remove selection styling */
          .pdf-section-wrapper {
            box-shadow: none !important;
          }

          /* Remove ring styling from selected sections */
          .pdf-section-wrapper.ring-2 {
            --tw-ring-offset-width: 0 !important;
            --tw-ring-width: 0 !important;
          }

          /* Hide "Selected" label */
          .pdf-section-wrapper > div.absolute.-left-2 {
            display: none !important;
          }

          /* Hide continuation indicators "(continued)" */
          .pdf-section-wrapper > div.absolute.top-0.left-0.right-0.bg-blue-100 {
            display: none !important;
          }

          @page {
            size: ${pageSettings.size} ${pageSettings.orientation};
            margin: 0;
          }

          /* Defeat any lingering scrollbars on matrix wrappers — the
             FitToWidth wrapper handles overscale by transforming the
             child, so the wrapper itself never needs to scroll. */
          .overflow-x-auto,
          .overflow-y-auto,
          .overflow-auto {
            overflow: visible !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PdfExportPreviewV2;
